<template>
    <div
      class="members-card-wrapper"
      :class="{ 'members-card-wrapper--split': !showFontSettings }"
    >
      <template v-if="showFontSettings">
        <div
          class="members-settings-card"
          :class="{ 'twitch-theme': isTwitchUser }"
        >
          <div class="members-settings-card-header">
            <h4 class="members-settings-card-title">{{ labels.title }}</h4>
          </div>
          <div class="members-settings-card-body">
            <form @submit.prevent="handleFontSave">
            <div class="row">
          <div class="col-md-12">
            <div class="form-group">
              <label for="fontName" class="control-label label-uppercase">{{ labels.fontName }}</label>
              <select
                id="fontName"
                v-model="fontSettings.font"
                class="form-control border-input"
              >
                <option value="Inter">Inter</option>
                <option value="Poppins">Poppins</option>
                <option value="Montserrat">Montserrat</option>
                <option value="Raleway">Raleway</option>
              </select>
            </div>
          </div>
        </div>

        <div class="row">
          <div class="col-md-6">
            <div class="form-group">
              <label for="fontColor" class="control-label label-uppercase">{{ labels.fontColor }}</label>
              <div class="font-color-input-wrap">
                <input
                  id="fontColor"
                  type="color"
                  v-model="fontSettings.color"
                  class="font-color-picker"
                />
                <input
                  type="text"
                  v-model="fontSettings.color"
                  class="form-control border-input font-color-hex"
                  placeholder="#000000"
                  @blur="validateHexColor"
                />
              </div>
              <small v-if="colorError" class="text-danger">{{ colorError }}</small>
              <small v-else class="form-text text-muted">{{ labels.hexColorHint }}</small>
            </div>
          </div>
          <div class="col-md-6">
            <div class="form-group">
              <label for="fontWeight" class="control-label label-uppercase">{{ labels.fontWeight }}</label>
              <select
                id="fontWeight"
                v-model="fontSettings.weight"
                class="form-control border-input"
              >
                <option value="thin">{{ labels.weightThin }}</option>
                <option value="extralight">{{ labels.weightExtraLight }}</option>
                <option value="light">{{ labels.weightLight }}</option>
                <option value="regular">{{ labels.weightRegular }}</option>
                <option value="medium">{{ labels.weightMedium }}</option>
                <option value="semibold">{{ labels.weightSemiBold }}</option>
                <option value="bold">{{ labels.weightBold }}</option>
                <option value="extrabold">{{ labels.weightExtraBold }}</option>
                <option value="black">{{ labels.weightBlack }}</option>
              </select>
            </div>
          </div>
        </div>

        <div class="d-flex justify-content-end mt-3">
          <button
            type="button"
            class="btn btn-primary"
            @click="handleFontSave"
            :disabled="isSavingFont || !!colorError"
          >
            {{ isSavingFont ? labels.saving : labels.save }}
          </button>
        </div>
      </form>

          <hr />

          <div class="language-section">
        <h5 class="language-section-title">{{ labels.languageTitle }}</h5>
        <p class="language-section-hint">{{ labels.languageHint }}</p>
        <div class="row align-items-end">
          <div class="col-md-8">
            <div class="form-group mb-0">
              <label for="defaultLanguage" class="control-label label-uppercase">{{ labels.languageLabel }}</label>
              <select
                id="defaultLanguage"
                v-model="languageOption"
                class="form-control border-input"
              >
                <option
                  v-for="option in languageOptions"
                  :key="option.value"
                  :value="option.value"
                >
                  {{ option.label }}
                </option>
              </select>
            </div>
          </div>
          <div class="col-md-4 mt-3 mt-md-0 d-flex justify-content-md-end">
            <button
              type="button"
              class="btn btn-primary"
              :disabled="isSavingLanguage || languageSubmitDisabled"
              @click="saveLanguagePreference"
            >
              {{ isSavingLanguage ? labels.saving : labels.save }}
            </button>
          </div>
        </div>
      </div>

      <hr v-if="showTimezoneBlock" />

      <div v-if="showTimezoneBlock" class="timezone-section">
        <h5 class="timezone-section-title">{{ labels.timezoneTitle }}</h5>
        <p class="timezone-section-hint">{{ labels.timezoneHint }}</p>
        <div class="row align-items-end">
          <div class="col-md-8">
            <div class="form-group mb-0">
              <label for="defaultTimezone" class="control-label label-uppercase">{{ labels.timezoneLabel }}</label>
              <select
                id="defaultTimezone"
                v-model="timezoneOption"
                class="form-control border-input"
                @focus="refreshTimezonePreviewTimes"
                @click="refreshTimezonePreviewTimes"
              >
                <option value="" disabled>{{ labels.selectTimezone }}</option>
                <option
                  v-for="option in timezoneOptions"
                  :key="option.value"
                  :value="option.value"
                >
                  {{ option.label }}
                </option>
              </select>
            </div>
          </div>
          <div class="col-md-4 mt-3 mt-md-0 d-flex justify-content-md-end">
            <button
              type="button"
              class="btn btn-primary"
              :disabled="isSavingTimezone || timezoneSubmitDisabled"
              @click="saveTimezonePreference"
            >
              {{ isSavingTimezone ? labels.saving : labels.save }}
            </button>
          </div>
        </div>
      </div>
          </div>
        </div>
      </template>
      <template v-else>
        <div
          class="members-settings-card"
          :class="{
            'twitch-theme': isTwitchUser,
            'members-settings-card--minimal': minimal,
          }"
        >
          <div v-if="!minimal" class="members-settings-card-header">
            <h4 class="members-settings-card-title">{{ labels.languageTitle }}</h4>
            <p class="members-settings-card-intro">{{ labels.languageHint }}</p>
          </div>
          <div
            class="members-settings-card-body"
            :class="{ 'members-settings-card-body--minimal': minimal }"
          >
            <div
              class="profile-language-row"
              :class="{ 'profile-language-row--minimal': minimal }"
            >
              <div v-if="minimal" class="profile-language-identity">
                <span class="profile-language-icon" aria-hidden="true">
                  <i class="ti-world"></i>
                </span>
                <label for="defaultLanguageSplit" class="profile-language-title">
                  {{ labels.languageTitle }}
                </label>
              </div>
              <label
                v-else
                for="defaultLanguageSplit"
                class="control-label label-uppercase"
              >
                {{ labels.languageLabel }}
              </label>
              <div class="profile-language-controls">
                <select
                  id="defaultLanguageSplit"
                  v-model="languageOption"
                  class="form-control border-input profile-language-select"
                  :aria-label="labels.languageTitle"
                  @change="onMinimalLanguageChange"
                >
                  <option
                    v-for="option in languageOptions"
                    :key="option.value"
                    :value="option.value"
                  >
                    {{ option.label }}
                  </option>
                </select>
                <button
                  v-if="!minimal"
                  type="button"
                  class="btn btn-primary profile-language-save"
                  :disabled="isSavingLanguage || languageSubmitDisabled"
                  @click="saveLanguagePreference"
                >
                  {{ isSavingLanguage ? labels.saving : labels.save }}
                </button>
                <span
                  v-else-if="isSavingLanguage"
                  class="profile-language-saving"
                  aria-live="polite"
                >
                  {{ labels.saving }}
                </span>
              </div>
            </div>
          </div>
        </div>
        <div
          v-if="showTimezoneBlock"
          class="members-settings-card"
          :class="{ 'twitch-theme': isTwitchUser }"
        >
          <div class="members-settings-card-header">
            <h4 class="members-settings-card-title">{{ labels.timezoneTitle }}</h4>
            <p class="members-settings-card-intro">{{ labels.timezoneHint }}</p>
          </div>
          <div class="members-settings-card-body">
            <div class="row align-items-end">
              <div class="col-md-8">
                <div class="form-group mb-0">
                  <label for="defaultTimezoneSplit" class="control-label label-uppercase">{{ labels.timezoneLabel }}</label>
                  <select
                    id="defaultTimezoneSplit"
                    v-model="timezoneOption"
                    class="form-control border-input"
                    @focus="refreshTimezonePreviewTimes"
                    @click="refreshTimezonePreviewTimes"
                  >
                    <option value="" disabled>{{ labels.selectTimezone }}</option>
                    <option
                      v-for="option in timezoneOptions"
                      :key="option.value"
                      :value="option.value"
                    >
                      {{ option.label }}
                    </option>
                  </select>
                </div>
              </div>
              <div class="col-md-4 mt-3 mt-md-0 d-flex justify-content-md-end">
                <button
                  type="button"
                  class="btn btn-primary"
                  :disabled="isSavingTimezone || timezoneSubmitDisabled"
                  @click="saveTimezonePreference"
                >
                  {{ isSavingTimezone ? labels.saving : labels.save }}
                </button>
              </div>
            </div>
          </div>
        </div>
      </template>
    </div>
</template>
<script>
import { supabase } from "@/supabase";
import { getEffectiveUserId, buildAuthHeaders } from "@/utils/impersonation";
import { initializeLocale } from "@/i18n";
import { useCurrentUserStore } from "@/stores/currentUser";
import { getApiUrl as resolveApiUrl } from "@/api/client";

export default {
  props: {
    isTwitchUser: {
      type: Boolean,
      default: false,
    },
    showFontSettings: {
      type: Boolean,
      default: true,
    },
    forceShowTimezone: {
      type: Boolean,
      default: false,
    },
    showTimezone: {
      type: Boolean,
      default: null,
    },
    minimal: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      isSavingFont: false,
      isLoadingFont: false,
      userId: null,
      fontSettings: {
        font: "Inter",
        color: "#000000",
        weight: "regular",
      },
      colorError: "",
      languageOption: "es",
      isSavingLanguage: false,
      timezoneOption: "",
      isSavingTimezone: false,
      timezonePreviewTimes: {},
    };
  },
  computed: {
    labels() {
      return {
        title: this.tt("userProfile.fontSettings.title", "Configuración de fuente"),
        fontName: this.tt("userProfile.fontSettings.fontName", "Nombre de la fuente"),
        fontColor: this.tt("userProfile.fontSettings.fontColor", "Color de la fuente"),
        fontWeight: this.tt("userProfile.fontSettings.fontWeight", "Peso de la fuente"),
        hexColorHint: this.tt("userProfile.fontSettings.hexColorHint", "Selecciona o introduce un código hex"),
        weightThin: this.tt("userProfile.fontSettings.weightThin", "Fino"),
        weightExtraLight: this.tt("userProfile.fontSettings.weightExtraLight", "Extra ligero"),
        weightLight: this.tt("userProfile.fontSettings.weightLight", "Ligero"),
        weightRegular: this.tt("userProfile.fontSettings.weightRegular", "Regular"),
        weightMedium: this.tt("userProfile.fontSettings.weightMedium", "Medio"),
        weightSemiBold: this.tt("userProfile.fontSettings.weightSemiBold", "Semi negrita"),
        weightBold: this.tt("userProfile.fontSettings.weightBold", "Negrita"),
        weightExtraBold: this.tt("userProfile.fontSettings.weightExtraBold", "Extra negrita"),
        weightBlack: this.tt("userProfile.fontSettings.weightBlack", "Negro"),
        saving: this.tt("common.saving", "Guardando..."),
        save: this.tt("userProfile.savePreferences", "Guardar"),
        hexColorError: this.tt("userProfile.fontSettings.hexColorError", "Introduce un color hex válido (ej., #000000)"),
        sessionError: this.tt("userProfile.sessionError", "Error: Sesión no encontrada. Por favor, inicia sesión de nuevo."),
        saveSuccess: this.tt("userProfile.fontSettings.saveSuccess", "¡Configuración guardada correctamente!"),
        saveError: this.tt("userProfile.fontSettings.saveError", "Error al guardar la configuración"),
        languageTitle: this.tt("userProfile.languageTitle", "Idioma de la interfaz"),
        languageHint: this.tt("userProfile.languageHint", "Elige el idioma que quieres usar en toda la plataforma."),
        languageLabel: this.tt("common.language", "Idioma"),
        languageSaved: this.tt("userProfile.languageSaved", "Idioma actualizado correctamente"),
        languageSaveError: this.tt("userProfile.languageSaveError", "Error al guardar el idioma"),
        timezoneTitle: this.tt("userProfile.timezoneTitle", "Zona horaria de programación"),
        timezoneHint: this.tt("userProfile.timezoneHint", "Define la zona horaria usada para programar clips."),
        timezoneLabel: this.tt("userProfile.timezoneLabel", "Zona horaria"),
        selectTimezone: this.tt("userProfile.selectTimezone", "Selecciona zona horaria"),
        timezoneSaved: this.tt("userProfile.timezoneSaved", "Zona horaria actualizada correctamente"),
        timezoneSaveError: this.tt("userProfile.timezoneSaveError", "Error al guardar la zona horaria"),
        timezoneRequired: this.tt("userProfile.timezoneRequired", "Selecciona una zona horaria"),
        spanish: this.tt("common.spanish", "Español"),
        english: this.tt("common.english", "Inglés"),
      };
    },
    languageOptions() {
      return [
        { value: "es", label: this.labels.spanish },
        { value: "en", label: this.labels.english },
      ];
    },
    timezoneOptions() {
      return [
        { value: "EST", baseLabel: "EST (Eastern Standard Time)" },
        { value: "PST", baseLabel: "PST (Pacific Standard Time)" },
        { value: "CST", baseLabel: "CST (Central Standard Time)" },
        { value: "MST", baseLabel: "MST (Mountain Standard Time)" },
        { value: "UTC", baseLabel: "UTC" },
      ].map((option) => {
        const previewTime = this.timezonePreviewTimes[option.value];
        return {
          value: option.value,
          label: previewTime ? `${option.baseLabel} - ${previewTime}` : option.baseLabel,
        };
      });
    },
    timezoneIanaMap() {
      return {
        EST: "Etc/GMT+5",
        CST: "Etc/GMT+6",
        MST: "Etc/GMT+7",
        PST: "Etc/GMT+8",
        UTC: "UTC",
      };
    },
    timezoneSubmitDisabled() {
      if (!this.timezoneOption) return true;
      const currentTimezone = String(useCurrentUserStore().timezone || "");
      return this.timezoneOption === currentTimezone;
    },
    languageSubmitDisabled() {
      if (!this.languageOption) return true;
      const currentLanguage = String(useCurrentUserStore().locale || "");
      return this.languageOption === currentLanguage;
    },
    showTimezoneBlock() {
      if (this.showTimezone === false) {
        return false;
      }
      if (this.showTimezone === true) {
        return true;
      }
      return this.isTwitchUser || this.forceShowTimezone;
    },
  },
  async mounted() {
    this.refreshTimezonePreviewTimes();
    await this.loadUserSession();
    if (this.userId) {
      await this.loadCurrentFontSettings();
    }
    const currentUserStore = useCurrentUserStore();
    if (!currentUserStore.profile) {
      await currentUserStore.ensureSessionReady();
    }
    this.languageOption = currentUserStore.locale;
    this.timezoneOption = currentUserStore.timezone || "";
    initializeLocale(currentUserStore.savedLocale);
  },
  methods: {
    tt(key, fallback) {
      return typeof this.$t === "function" ? this.$t(key) : fallback;
    },
    refreshTimezonePreviewTimes() {
      const now = new Date();
      const nextPreviewTimes = {};
      Object.entries(this.timezoneIanaMap).forEach(([abbreviation, ianaTimezone]) => {
        nextPreviewTimes[abbreviation] = new Intl.DateTimeFormat("en-US", {
          timeZone: ianaTimezone,
          hour: "2-digit",
          minute: "2-digit",
          hour12: true,
        }).format(now);
      });
      this.timezonePreviewTimes = nextPreviewTimes;
    },
    async loadUserSession() {
      try {
        const { data: { session } } = await supabase.auth.getSession();
        if (session && session.user) {
          this.userId = getEffectiveUserId(session.user.id);
        }
      } catch (error) {
        console.error("Error loading session:", error);
      }
    },
    async loadCurrentFontSettings() {
      if (!this.userId) return;
      
      this.isLoadingFont = true;
      try {
        const { data, error } = await supabase
          .from("user_info")
          .select("use_font, use_font_color, use_font_weight")
          .eq("user_id", this.userId)
          .maybeSingle();

        if (error && error.code !== 'PGRST116') {
          console.error("Error loading font settings:", error);
          return;
        }

        if (data) {
          this.fontSettings = {
            font: data.use_font || "Inter",
            color: data.use_font_color || "#000000",
            weight: data.use_font_weight || "regular",
          };
        }
      } catch (error) {
        console.error("Error loading font settings:", error);
      } finally {
        this.isLoadingFont = false;
      }
    },
    validateHexColor() {
      const hexPattern = /^#([A-Fa-f0-9]{6})$/;
      if (this.fontSettings.color && !hexPattern.test(this.fontSettings.color)) {
        this.colorError = this.labels.hexColorError;
        return false;
      }
      this.colorError = "";
      return true;
    },
    handleFontSave() {
      if (!this.validateHexColor()) {
        return;
      }
      this.confirmAndSave();
    },
    async confirmAndSave() {
      if (!this.userId) {
        this.$notify({
          message: this.labels.sessionError,
          icon: "ti-alert",
          horizontalAlign: "right",
          verticalAlign: "top",
          type: "danger",
        });
        return;
      }

      this.isSavingFont = true;
      try {
        const updateData = {
          use_font: this.fontSettings.font || "Inter",
          use_font_color: this.fontSettings.color || "#000000",
          use_font_weight: this.fontSettings.weight || "regular",
        };

        // First, try to update existing record
        const { data: updateResult, error: updateError } = await supabase
          .from("user_info")
          .update(updateData)
          .eq("user_id", this.userId)
          .select();

        // If update didn't affect any rows, insert new record
        if (!updateError && (!updateResult || updateResult.length === 0)) {
          const insertData = {
            user_id: this.userId,
            ...updateData,
          };
          
          const { error: insertError } = await supabase
            .from("user_info")
            .insert(insertData);

          if (insertError) {
            throw insertError;
          }
        } else if (updateError) {
          throw updateError;
        }

        this.$notify({
          message: this.labels.saveSuccess,
          icon: "ti-check",
          horizontalAlign: "right",
          verticalAlign: "top",
          type: "success",
        });
      } catch (error) {
        console.error("Error saving font settings:", error);
        this.$notify({
          message: this.labels.saveError + ": " + (error.message || error),
          icon: "ti-alert",
          horizontalAlign: "right",
          verticalAlign: "top",
          type: "danger",
        });
      } finally {
        this.isSavingFont = false;
      }
    },
    onMinimalLanguageChange() {
      if (!this.minimal || this.isSavingLanguage || this.languageSubmitDisabled) {
        return;
      }
      this.saveLanguagePreference();
    },
    async saveLanguagePreference() {
      if (this.isSavingLanguage || this.languageSubmitDisabled) {
        return;
      }
      if (!this.userId) {
        this.$notify({
          message: this.labels.sessionError,
          icon: "ti-alert",
          horizontalAlign: "right",
          verticalAlign: "top",
          type: "danger",
        });
        return;
      }
      this.isSavingLanguage = true;
      try {
        const { data: { session } } = await supabase.auth.getSession();
        if (!session) {
          throw new Error(this.labels.sessionError);
        }
        const apiUrl = resolveApiUrl();
        const response = await fetch(`${apiUrl}/momo/get-users/user-default-lenguage`, {
          method: "PATCH",
          headers: buildAuthHeaders(session),
          body: JSON.stringify({
            user_id: this.userId,
            user_default_lenguage: this.languageOption,
          }),
        });
        if (!response.ok) {
          const errData = await response.json().catch(() => null);
          throw new Error(errData?.detail || response.statusText || this.labels.languageSaveError);
        }
        await useCurrentUserStore().refresh(session);
        this.languageOption = useCurrentUserStore().locale;
        initializeLocale(useCurrentUserStore().savedLocale);
        this.$notify({
          message: this.labels.languageSaved,
          icon: "ti-check",
          horizontalAlign: "right",
          verticalAlign: "top",
          type: "success",
        });
      } catch (error) {
        console.error("Error saving language preference:", error);
        this.$notify({
          message: `${this.labels.languageSaveError}: ${error.message || error}`,
          icon: "ti-alert",
          horizontalAlign: "right",
          verticalAlign: "top",
          type: "danger",
        });
      } finally {
        this.isSavingLanguage = false;
      }
    },
    async saveTimezonePreference() {
      if (!this.timezoneOption) {
        this.$notify({
          message: this.labels.timezoneRequired,
          icon: "ti-alert",
          horizontalAlign: "right",
          verticalAlign: "top",
          type: "danger",
        });
        return;
      }
      if (this.timezoneSubmitDisabled || this.isSavingTimezone) {
        return;
      }
      this.isSavingTimezone = true;
      try {
        const { data: { session } } = await supabase.auth.getSession();
        if (!session) {
          throw new Error(this.labels.sessionError);
        }
        const apiUrl = resolveApiUrl();
        const response = await fetch(`${apiUrl}/momo/twitch/user-info/timezone`, {
          method: "PATCH",
          headers: buildAuthHeaders(session),
          body: JSON.stringify({
            use_timezone: this.timezoneOption,
          }),
        });
        if (!response.ok) {
          const errData = await response.json().catch(() => null);
          throw new Error(errData?.detail || response.statusText || this.labels.timezoneSaveError);
        }
        await useCurrentUserStore().refresh(session);
        this.timezoneOption = useCurrentUserStore().timezone || this.timezoneOption;
        this.$notify({
          message: this.labels.timezoneSaved,
          icon: "ti-check",
          horizontalAlign: "right",
          verticalAlign: "top",
          type: "success",
        });
      } catch (error) {
        console.error("Error saving timezone preference:", error);
        this.$notify({
          message: `${this.labels.timezoneSaveError}: ${error.message || error}`,
          icon: "ti-alert",
          horizontalAlign: "right",
          verticalAlign: "top",
          type: "danger",
        });
      } finally {
        this.isSavingTimezone = false;
      }
    },
  },
};
</script>
<style lang="scss" scoped>
@import "@/assets/sass/paper/variables";

/* Estilo base - tema claro con acentos cítricos (como SocialConnectionsCard) */
.members-settings-card {
  background: #ffffff;
  border: 1px solid rgba(0, 0, 0, 0.08);
  border-radius: 12px;
  color: #212529;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.08), 0 0 20px var(--clipr-citrus-glow);
  overflow: hidden;
}

.members-settings-card-header {
  padding: 20px 20px 0;
  border-bottom: 0;
}

.members-settings-card-title {
  margin: 0;
  font-weight: 600;
  font-size: 1.1rem;
  color: #212529;
}

.members-settings-card-intro {
  margin: 0.5rem 0 0;
  font-size: 0.9rem;
  color: #6c757d;
  line-height: 1.45;
}

.members-card-wrapper {
  display: flex;
  flex-direction: column;
  min-width: 0;
}

.members-card-wrapper--split {
  gap: 1.25rem;
}

.members-settings-card--minimal {
  box-shadow: none;
}

.members-settings-card-body--minimal {
  padding: 0;
}

.profile-language-row {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.profile-language-row--minimal {
  gap: 0.65rem;
}

@media (min-width: 576px) {
  .profile-language-row--minimal {
    flex-direction: row;
    align-items: center;
    justify-content: space-between;
    gap: 1rem;
  }
}

.profile-language-label {
  margin: 0;
  font-size: 0.82rem;
  font-weight: 500;
  color: var(--clipr-text-secondary);
  flex-shrink: 0;
}

.profile-language-identity {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  min-width: 0;
  flex-shrink: 0;
}

.profile-language-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 38px;
  height: 38px;
  border-radius: 11px;
  background: rgba(255, 255, 255, 0.045);
  border: 1px solid rgba(148, 163, 184, 0.14);
  color: var(--clipr-text-secondary);
  font-size: 15px;
  flex-shrink: 0;
}

.profile-language-title {
  margin: 0;
  font-size: 0.875rem;
  font-weight: 600;
  color: var(--clipr-text-primary);
  cursor: pointer;
  white-space: nowrap;
}

.profile-language-controls {
  display: flex;
  align-items: center;
  gap: 0.65rem;
  width: 100%;
}

.profile-language-row--minimal .profile-language-controls {
  width: 100%;
}

@media (min-width: 576px) {
  .profile-language-row--minimal .profile-language-controls {
    width: auto;
    flex: 0 1 240px;
    justify-content: flex-end;
    margin-left: auto;
  }
}

.profile-language-saving {
  flex-shrink: 0;
  font-size: 0.78rem;
  color: var(--clipr-text-secondary);
  padding-right: 0.15rem;
}

.profile-language-select {
  flex: 1;
  min-width: 0;
}

.profile-language-save {
  flex-shrink: 0;
  min-width: 5.5rem;
}

.members-settings-card-body {
  padding: 15px 20px 20px;
}

.members-settings-card label,
.members-settings-card .form-text,
.members-settings-card .text-muted {
  color: #6c757d;
}

.members-settings-card .form-control,
.members-settings-card .border-input {
  background: #ffffff;
  border-color: #dee2e6;
  color: #212529;
}

.border-input {
  border: 1px solid $table-line-color;
}

.font-color-input-wrap {
  position: relative;
  display: block;
  width: 100%;
}

.font-color-picker {
  position: absolute;
  left: 10px;
  top: 50%;
  transform: translateY(-50%);
  width: 24px;
  height: 24px;
  border-radius: 50%;
  padding: 0;
  border: 2px solid $table-line-color;
  cursor: pointer;
  z-index: 2;
  -webkit-appearance: none;
  appearance: none;
  background: transparent;
}

.font-color-picker::-webkit-color-swatch-wrapper {
  padding: 0;
}

.font-color-picker::-webkit-color-swatch {
  border-radius: 50%;
  border: none;
}

.font-color-picker::-moz-color-swatch {
  border-radius: 50%;
  border: none;
}

.font-color-hex {
  padding-left: 44px;
}

.label-uppercase {
  text-transform: uppercase;
  font-size: 0.75rem;
  letter-spacing: 0.02em;
}

.mt-3 {
  margin-top: 1rem;
}

.language-section {
  margin-top: 1.25rem;
}

.language-section-title {
  margin: 0 0 0.35rem;
  font-size: 1rem;
  font-weight: 600;
}

.language-section-hint {
  margin: 0 0 1rem;
}

.timezone-section {
  margin-top: 1.25rem;
}

.timezone-section-title {
  margin: 0 0 0.35rem;
  font-size: 1rem;
  font-weight: 600;
}

.timezone-section-hint {
  margin: 0 0 1rem;
}

.members-settings-card-body > hr {
  margin: 1.25rem 0;
}

.members-settings-card .form-group {
  margin-bottom: 1rem;
}

.members-settings-card .btn {
  min-height: 40px;
  padding: 0.55rem 1rem;
}

@media (max-width: 991px) {
  .members-settings-card-header {
    padding: 1rem 1rem 0;
  }

  .members-settings-card-body {
    padding: 0.9rem 1rem 1rem;
  }

  .language-section,
  .timezone-section {
    margin-top: 1rem;
  }

  .language-section-hint,
  .timezone-section-hint {
    margin-bottom: 0.85rem;
  }
}

@media (max-width: 575px) {
  .members-settings-card-header {
    padding-left: 1rem;
    padding-right: 1rem;
  }

  .members-settings-card-body {
    padding-left: 1rem;
    padding-right: 1rem;
  }

  .members-settings-card .row {
    margin-left: 0;
    margin-right: 0;
  }

  .members-settings-card .row > [class*="col-"] {
    padding-left: 0;
    padding-right: 0;
  }

  .members-settings-card .row.align-items-end {
    row-gap: 0.85rem;
  }

  .members-settings-card .d-flex.justify-content-end.mt-3,
  .members-settings-card .col-md-4.mt-3.mt-md-0.d-flex.justify-content-md-end {
    justify-content: stretch !important;
  }

  .members-settings-card .btn {
    width: 100%;
  }
}

/* Tema Twitch - púrpura (como SocialConnectionsCard) */
.members-settings-card.twitch-theme {
  background: var(--clipr-purple-overlay-medium);
  border: 1px solid var(--clipr-border-color);
  color: var(--clipr-text-primary);
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.5);
}

.members-settings-card.twitch-theme .members-settings-card-title {
  color: var(--clipr-text-primary);
}

.members-settings-card.twitch-theme .members-settings-card-intro {
  color: var(--clipr-text-secondary);
}

.members-settings-card.twitch-theme label,
.members-settings-card.twitch-theme .form-text,
.members-settings-card.twitch-theme .text-muted {
  color: var(--clipr-text-secondary);
}

.members-settings-card.twitch-theme .form-control,
.members-settings-card.twitch-theme .border-input {
  background: var(--clipr-bg-elevated);
  border-color: var(--clipr-border-color);
  color: var(--clipr-text-primary);
}

.members-settings-card.twitch-theme .font-color-picker {
  border-color: var(--clipr-border-color);
}

.members-settings-card.twitch-theme .btn-primary {
  background: var(--clipr-purple-gradient);
  border: none;
  box-shadow: 0 4px 14px var(--clipr-purple-glow);
  border-radius: 8px;
}

.members-settings-card.twitch-theme .btn-primary:hover:not(:disabled) {
  background: linear-gradient(135deg, #a855f7 0%, #d946ef 100%);
  box-shadow: var(--clipr-shadow-button-hover);
}

.members-settings-card.twitch-theme .language-section-title {
  color: var(--clipr-text-primary);
}

.members-settings-card.twitch-theme .language-section-hint {
  color: var(--clipr-text-secondary);
}

.members-settings-card.twitch-theme .timezone-section-title {
  color: var(--clipr-text-primary);
}

.members-settings-card.twitch-theme .timezone-section-hint {
  color: var(--clipr-text-secondary);
}
</style>
